import { useState } from 'react';
import { DeckState, Position } from '@/types/game';
import { importDeckFromJson } from '@/utils/deckLoader';
import JsonDeckImporter from './JsonDeckImporter';

interface DeckImporterProps {
  onImport: (deck: DeckState) => void;
  position?: Position;
}

export default function DeckImporter({ onImport, position = { x: 200, y: 200 } }: DeckImporterProps) {
  const [deckJson, setDeckJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importMethod, setImportMethod] = useState<'text' | 'file'>('text');
  const [error, setError] = useState<string | null>(null);
  
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        setDeckJson(content);
      } catch (error) {
        console.error("Error reading file:", error);
        setError('Failed to read the file. Please try again.');
      }
    };
    
    reader.onerror = () => {
      setError('Failed to read the file. Please try again.');
    };
    
    reader.readAsText(file);
  };
  
  const handleFileImport = async () => {
    if (!deckJson.trim()) {
      setError('Please upload a file');
      return;
    }
    
    setError(null);
    setIsImporting(true);
    
    try {
      // Use the shared importDeckFromJson function
      const deck = importDeckFromJson(deckJson, position);
      onImport(deck);
      setDeckJson('');
    } catch (error) {
      console.error("Error importing deck from file:", error);
      setError(error instanceof Error ? error.message : 'Failed to import deck from file. Please check the file format.');
    } finally {
      setIsImporting(false);
    }
  };
  
  return (
    <div>
      <div className="mb-4">
        <div className="flex space-x-2 mb-4">
          <button
            className={`px-4 py-2 rounded ${importMethod === 'text' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
            onClick={() => setImportMethod('text')}
          >
            Paste JSON
          </button>
          <button
            className={`px-4 py-2 rounded ${importMethod === 'file' ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`}
            onClick={() => setImportMethod('file')}
          >
            Upload File
          </button>
        </div>
        
        {importMethod === 'text' ? (
          <JsonDeckImporter onImport={onImport} position={position} />
        ) : (
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Upload a JSON file containing your deck data.
            </p>
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
            />
            {deckJson && (
              <div className="mt-2">
                <p className="text-sm text-green-600 dark:text-green-400">File loaded successfully!</p>
              </div>
            )}
            
            {error && (
              <div className="mt-2 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}
            
            <div className="flex justify-end mt-4">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded shadow disabled:bg-blue-300 dark:disabled:bg-blue-700/50"
                onClick={handleFileImport}
                disabled={isImporting || !deckJson.trim()}
              >
                {isImporting ? 'Importing...' : 'Import Deck'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
} 
